import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tab,
  Tabs,
  Button,
  InputBase,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  CloudUpload as UploadIcon,
  ImageOutlined as ImageIcon,
  FolderOutlined as FolderIcon,
  InsertDriveFileOutlined as FileIcon,
} from '@mui/icons-material';
import FolderBrowser from '../components/media/FolderBrowser';
import FileUploader from '../components/media/FileUploader';
import FileDetails from '../components/media/FileDetails';
import { MediaFile, MediaImage } from '../models/media';

const MediaManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'images' | 'documents'>('all');
  const [uploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false);
  const [currentFolder, setCurrentFolder] = useState<number | null | undefined>(undefined);
  const [fileDetailsOpen, setFileDetailsOpen] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<MediaFile | MediaImage | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: 'all' | 'images' | 'documents') => {
    setActiveTab(newValue);
  };
  
  const handleFileSelect = (file: MediaFile | MediaImage) => {
    setSelectedFile(file);
    setFileDetailsOpen(true);
  };
  
  const handleFileUpdate = (file: MediaFile | MediaImage) => {
    // Update the file in the list or simply close and refresh
    setSelectedFile(file);
  };
  
  // Handler for folder navigation
  const handleFolderChange = (folderId: number | null | undefined) => {
    setCurrentFolder(folderId);
  };
  
  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Media Management
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Upload, organize, and manage your media files in hierarchical folders
        </Typography>
        {currentFolder === null && (
          <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
            Viewing root folder
          </Typography>
        )}
      </Box>
      
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2
        }}>
          {/* Search */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            px: 1,
            flex: { xs: '1', sm: '0 1 auto' },
            width: { xs: '100%', sm: '300px' }
          }}>
            <InputBase
              placeholder={`Search ${currentFolder !== undefined ? 'in current folder' : 'all media'}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ ml: 1, flex: 1 }}
            />
            <IconButton size="small">
              <SearchIcon />
            </IconButton>
          </Box>
          
          {/* Filter and Upload */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              size="small"
            >
              Filters
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<UploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
            >
              Upload
            </Button>
          </Box>
        </Box>
        
        <Divider />
        
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="media type tabs"
          variant="fullWidth"
        >
          <Tab 
            label="All Files" 
            value="all" 
            icon={<FolderIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Images" 
            value="images" 
            icon={<ImageIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Documents" 
            value="documents" 
            icon={<FileIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <FolderBrowser
          onSelectFile={handleFileSelect}
          onUploadClick={() => setUploadDialogOpen(true)}
          fileTypeFilter={activeTab}
          currentFolderId={currentFolder}
          onFolderChange={handleFolderChange}
        />
      </Paper>
      
      {/* Upload Dialog */}
      <FileUploader
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        currentFolderId={currentFolder}
        onUploadComplete={() => {
          // Close the dialog and trigger a refresh by changing the current folder
          setUploadDialogOpen(false);
          
          // Force refresh by briefly setting to undefined and back to current value
          const currentValue = currentFolder;
          setCurrentFolder(undefined);
          setTimeout(() => {
            setCurrentFolder(currentValue);
          }, 50);
        }}
      />
      
      {/* File Details Dialog */}
      {selectedFile && (
        <FileDetails
          file={selectedFile}
          open={fileDetailsOpen}
          onClose={() => setFileDetailsOpen(false)}
          onUpdate={handleFileUpdate}
        />
      )}
    </Container>
  );
};

export default MediaManagement;
